/**
 * Templates & Placeholder Names
 * Building/district template catalogs and per-tier generation rules.
 */

import type {
  BuildingCategory,
  BuildingTemplate,
  BuildingSize,
  BuildingService,
  DistrictTemplate,
  DistrictType,
  TierTemplate,
  SettlementTier,
  VisualStyle,
} from './types';

// ============================================================================
// BUILDING TEMPLATES
// ============================================================================

const svc = (type: BuildingService['type'], description: string): BuildingService => ({ type, description });

const style = (
  material: VisualStyle['material'],
  roofStyle: VisualStyle['roofStyle'],
  stories: number,
): VisualStyle => ({ material, roofStyle, stories });

export const BUILDING_TEMPLATES: Record<BuildingCategory, BuildingTemplate> = {
  tavern: {
    category: 'tavern',
    size: 'medium',
    placeholderNames: ['The Tavern', 'The Alehouse', 'The Flagon', 'The Mug & Barrel'],
    services: [svc('rest', 'Cheap lodging'), svc('info', 'Rumors and gossip'), svc('trade', 'Food and drink')],
    npcRoles: ['tavernkeeper', 'barmaid'],
    preferredDistricts: ['center', 'market', 'slums', 'docks'],
    visualStyle: style('wood', 'thatch', 2),
    tags: ['social', 'lodging', 'drink'],
  },
  inn: {
    category: 'inn',
    size: 'medium',
    placeholderNames: ['The Inn', 'The Wayfarers Rest', 'The Roadside Lodge', 'The Pilgrim House'],
    services: [svc('rest', 'Comfortable rooms'), svc('heal', 'Hot meals restore vitals'), svc('info', 'Traveler news')],
    npcRoles: ['innkeeper'],
    preferredDistricts: ['center', 'market'],
    visualStyle: style('stone', 'tile', 2),
    tags: ['social', 'lodging', 'comfort'],
  },
  temple: {
    category: 'temple',
    size: 'large',
    placeholderNames: ['The Temple', 'The Great Sanctum', 'The Hall of Faith', 'The Cathedral'],
    services: [svc('heal', 'Divine healing'), svc('quest', 'Holy missions'), svc('info', 'Religious guidance')],
    npcRoles: ['high priest', 'acolyte'],
    preferredDistricts: ['temple', 'center', 'noble'],
    visualStyle: style('stone', 'slate', 2),
    tags: ['religious', 'healing', 'authority'],
  },
  shrine: {
    category: 'shrine',
    size: 'small',
    placeholderNames: ['The Shrine', 'The Wayside Altar', 'The Spirit Stone', 'The Prayer Nook'],
    services: [svc('heal', 'Minor blessings')],
    npcRoles: ['shrine keeper'],
    preferredDistricts: ['temple', 'residential', 'center'],
    visualStyle: style('stone', 'flat', 1),
    tags: ['religious', 'small'],
  },
  market: {
    category: 'market',
    size: 'large',
    placeholderNames: ['The Market', 'Market Square', 'The Bazaar', 'The Trading Post'],
    services: [svc('trade', 'General goods'), svc('info', 'Merchant news')],
    npcRoles: ['market master', 'merchant'],
    preferredDistricts: ['market', 'center'],
    visualStyle: style('wood', 'thatch', 1),
    tags: ['commerce', 'social', 'open-air'],
  },
  blacksmith: {
    category: 'blacksmith',
    size: 'medium',
    placeholderNames: ['The Smithy', 'The Forge', 'The Anvil', 'The Ironworks'],
    services: [svc('repair', 'Weapon and armor repair'), svc('trade', 'Arms and armor')],
    npcRoles: ['blacksmith'],
    preferredDistricts: ['craftsmen', 'market', 'garrison'],
    visualStyle: style('stone', 'flat', 1),
    tags: ['craft', 'weapons', 'hot'],
  },
  apothecary: {
    category: 'apothecary',
    size: 'small',
    placeholderNames: ['The Apothecary', 'The Herb Shop', 'The Potion Maker', 'The Remedy'],
    services: [svc('heal', 'Potions and salves'), svc('trade', 'Alchemical supplies')],
    npcRoles: ['apothecary'],
    preferredDistricts: ['market', 'temple', 'center'],
    visualStyle: style('wood', 'tile', 2),
    tags: ['healing', 'alchemy', 'trade'],
  },
  guild_hall: {
    category: 'guild_hall',
    size: 'large',
    placeholderNames: ['The Guild Hall', 'The Guildhall', 'The Adventurers Lodge', 'The Hall of Trades'],
    services: [svc('quest', 'Job postings'), svc('info', 'Professional contacts'), svc('trade', 'Specialized goods')],
    npcRoles: ['guildmaster', 'clerk'],
    preferredDistricts: ['center', 'market', 'craftsmen'],
    visualStyle: style('stone', 'slate', 2),
    tags: ['authority', 'social', 'quest'],
  },
  guard_post: {
    category: 'guard_post',
    size: 'small',
    placeholderNames: ['The Guard Post', 'The Watch Tower', 'The Patrol Station', 'The Sentry Hut'],
    services: [svc('info', 'Safety reports'), svc('quest', 'Bounties')],
    npcRoles: ['guard captain'],
    preferredDistricts: ['garrison', 'center', 'market'],
    visualStyle: style('stone', 'flat', 1),
    tags: ['military', 'law', 'small'],
  },
  barracks: {
    category: 'barracks',
    size: 'large',
    placeholderNames: ['The Barracks', 'The Garrison', 'The Soldiers Quarters', 'The War Hall'],
    services: [svc('quest', 'Military contracts'), svc('repair', 'Armory services')],
    npcRoles: ['commander', 'quartermaster'],
    preferredDistricts: ['garrison', 'center'],
    visualStyle: style('stone', 'flat', 2),
    tags: ['military', 'authority', 'large'],
  },
  keep: {
    category: 'keep',
    size: 'large',
    placeholderNames: ['The Keep', 'The Stronghold', 'The Fortress', 'The Watchtower'],
    services: [svc('quest', 'Lord tasks'), svc('info', 'Official decrees')],
    npcRoles: ['lord', 'steward'],
    preferredDistricts: ['center', 'noble', 'garrison'],
    visualStyle: style('stone', 'slate', 3),
    tags: ['authority', 'military', 'fortified'],
  },
  castle: {
    category: 'castle',
    size: 'large',
    placeholderNames: ['The Castle', 'The Citadel', 'The Palace', 'The Royal Hold'],
    services: [svc('quest', 'Royal missions'), svc('info', 'Court politics')],
    npcRoles: ['ruler', 'advisor', 'chamberlain'],
    preferredDistricts: ['center', 'noble'],
    visualStyle: style('stone', 'slate', 4),
    tags: ['authority', 'royalty', 'fortified', 'grand'],
  },
  warehouse: {
    category: 'warehouse',
    size: 'large',
    placeholderNames: ['The Warehouse', 'The Storehouse', 'The Depot', 'The Granary'],
    services: [svc('trade', 'Bulk goods storage')],
    npcRoles: ['warehouse keeper'],
    preferredDistricts: ['docks', 'market', 'craftsmen'],
    visualStyle: style('wood', 'flat', 1),
    tags: ['commerce', 'storage', 'large'],
  },
  stable: {
    category: 'stable',
    size: 'medium',
    placeholderNames: ['The Stables', 'The Livery', 'The Horse Pen', 'The Paddock'],
    services: [svc('trade', 'Mount purchase and care'), svc('rest', 'Animal boarding')],
    npcRoles: ['stablemaster'],
    preferredDistricts: ['market', 'residential', 'center'],
    visualStyle: style('wood', 'thatch', 1),
    tags: ['animals', 'transport'],
  },
  library: {
    category: 'library',
    size: 'medium',
    placeholderNames: ['The Library', 'The Archives', 'The Athenaeum', 'The Scroll House'],
    services: [svc('info', 'Research and lore'), svc('quest', 'Lost knowledge')],
    npcRoles: ['librarian', 'scribe'],
    preferredDistricts: ['temple', 'noble', 'center'],
    visualStyle: style('stone', 'tile', 2),
    tags: ['knowledge', 'quiet', 'scholarly'],
  },
  well: {
    category: 'well',
    size: 'small',
    placeholderNames: ['The Well', 'The Town Well', 'The Spring', 'The Water Pump'],
    services: [],
    npcRoles: [],
    preferredDistricts: ['center', 'residential', 'market'],
    visualStyle: style('stone', 'flat', 1),
    tags: ['utility', 'water', 'landmark'],
  },
  fountain: {
    category: 'fountain',
    size: 'small',
    placeholderNames: ['The Fountain', 'The Town Fountain', 'The Grand Fountain', 'The Water Garden'],
    services: [],
    npcRoles: [],
    preferredDistricts: ['center', 'noble', 'temple'],
    visualStyle: style('stone', 'flat', 1),
    tags: ['decorative', 'water', 'landmark'],
  },
  gate: {
    category: 'gate',
    size: 'medium',
    placeholderNames: ['The Gate', 'The Main Gate', 'The Town Gate', 'The Gatehouse'],
    services: [],
    npcRoles: ['gate guard'],
    preferredDistricts: ['garrison', 'center'],
    visualStyle: style('stone', 'flat', 2),
    tags: ['fortified', 'entry', 'military'],
  },
  tower: {
    category: 'tower',
    size: 'small',
    placeholderNames: ['The Tower', 'The Watchtower', 'The Spire', 'The Lookout'],
    services: [],
    npcRoles: ['watchman'],
    preferredDistricts: ['garrison', 'noble', 'center'],
    visualStyle: style('stone', 'slate', 3),
    tags: ['military', 'tall', 'defensive'],
  },
  dock: {
    category: 'dock',
    size: 'medium',
    placeholderNames: ['The Docks', 'The Wharf', 'The Pier', 'The Landing'],
    services: [svc('trade', 'River/sea goods'), svc('info', 'Sailor rumors')],
    npcRoles: ['harbormaster'],
    preferredDistricts: ['docks'],
    visualStyle: style('wood', 'flat', 1),
    tags: ['water', 'commerce', 'transport'],
  },
  farm: {
    category: 'farm',
    size: 'large',
    placeholderNames: ['The Farm', 'The Homestead', 'The Ranch', 'The Fields'],
    services: [svc('trade', 'Fresh produce'), svc('rest', 'Barn lodging')],
    npcRoles: ['farmer'],
    preferredDistricts: ['farmland', 'residential'],
    visualStyle: style('wood', 'thatch', 1),
    tags: ['agriculture', 'rural', 'food'],
  },
  mill: {
    category: 'mill',
    size: 'medium',
    placeholderNames: ['The Mill', 'The Windmill', 'The Watermill', 'The Gristmill'],
    services: [svc('trade', 'Flour and grain')],
    npcRoles: ['miller'],
    preferredDistricts: ['farmland', 'craftsmen'],
    visualStyle: style('wood', 'thatch', 2),
    tags: ['craft', 'agriculture', 'utility'],
  },
  tannery: {
    category: 'tannery',
    size: 'medium',
    placeholderNames: ['The Tannery', 'The Leather Works', 'The Hide Shop', 'The Curing House'],
    services: [svc('trade', 'Leather goods'), svc('repair', 'Leather armor repair')],
    npcRoles: ['tanner'],
    preferredDistricts: ['craftsmen', 'slums'],
    visualStyle: style('wood', 'flat', 1),
    tags: ['craft', 'smelly', 'industrial'],
  },
  residence: {
    category: 'residence',
    size: 'small',
    placeholderNames: ['A House', 'A Cottage', 'A Dwelling', 'A Home'],
    services: [],
    npcRoles: [],
    preferredDistricts: ['residential', 'slums', 'farmland'],
    visualStyle: style('wood', 'thatch', 1),
    tags: ['domestic', 'common'],
  },
  manor: {
    category: 'manor',
    size: 'large',
    placeholderNames: ['The Manor', 'The Estate', 'The Grand House', 'The Lord House'],
    services: [svc('quest', 'Noble patronage'), svc('info', 'Political intrigue')],
    npcRoles: ['noble', 'servant'],
    preferredDistricts: ['noble', 'center'],
    visualStyle: style('stone', 'tile', 2),
    tags: ['wealthy', 'authority', 'grand'],
  },
  ruin: {
    category: 'ruin',
    size: 'medium',
    placeholderNames: ['The Ruins', 'The Old Foundation', 'The Collapsed Hall', 'The Rubble'],
    services: [svc('quest', 'Explore the mystery')],
    npcRoles: [],
    preferredDistricts: ['slums', 'residential', 'farmland'],
    visualStyle: style('stone', 'flat', 1),
    tags: ['abandoned', 'mysterious', 'dangerous'],
  },
  monument: {
    category: 'monument',
    size: 'small',
    placeholderNames: ['The Monument', 'The Statue', 'The Obelisk', 'The Memorial'],
    services: [svc('info', 'Historical knowledge')],
    npcRoles: [],
    preferredDistricts: ['center', 'noble', 'temple'],
    visualStyle: style('stone', 'flat', 1),
    tags: ['landmark', 'decorative', 'historical'],
  },
  graveyard: {
    category: 'graveyard',
    size: 'large',
    placeholderNames: ['The Graveyard', 'The Cemetery', 'The Bone Yard', 'The Resting Place'],
    services: [svc('quest', 'Undead threats'), svc('info', 'Ancient lineages')],
    npcRoles: ['gravedigger'],
    preferredDistricts: ['temple', 'slums', 'residential'],
    visualStyle: style('stone', 'flat', 1),
    tags: ['death', 'spooky', 'quiet'],
  },
  general_store: {
    category: 'general_store',
    size: 'medium',
    placeholderNames: ['The General Store', 'The Supply Shop', 'The Emporium', 'The Trading Post'],
    services: [svc('trade', 'Adventuring supplies'), svc('info', 'Local tips')],
    npcRoles: ['shopkeeper'],
    preferredDistricts: ['market', 'center', 'residential'],
    visualStyle: style('wood', 'tile', 1),
    tags: ['commerce', 'supplies', 'social'],
  },
};

// ============================================================================
// DISTRICT TEMPLATES
// ============================================================================

export const DISTRICT_TEMPLATES: Record<DistrictType, DistrictTemplate> = {
  center: {
    type: 'center',
    zone: 0,
    atmospheres: ['bustling crossroads', 'heart of the settlement', 'busy town square'],
    placeholderNames: ['Town Center', 'Central Square', 'The Commons'],
  },
  market: {
    type: 'market',
    zone: 1,
    atmospheres: ['lively marketplace', 'cacophony of merchants', 'colorful stalls and carts'],
    placeholderNames: ['Market District', 'The Bazaar', 'Merchant Quarter'],
  },
  residential: {
    type: 'residential',
    zone: 2,
    atmospheres: ['quiet homes and gardens', 'children playing in lanes', 'wash lines and chimney smoke'],
    placeholderNames: ['Residential Quarter', 'The Homes', 'Hearthside'],
  },
  noble: {
    type: 'noble',
    zone: 1,
    atmospheres: ['grand facades and iron gates', 'manicured gardens', 'quiet affluence'],
    placeholderNames: ['Noble Quarter', 'The Heights', 'High Town'],
  },
  temple: {
    type: 'temple',
    zone: 1,
    atmospheres: ['incense and prayer bells', 'solemn processions', 'hallowed ground'],
    placeholderNames: ['Temple District', 'The Sanctum', 'Holy Quarter'],
  },
  slums: {
    type: 'slums',
    zone: 3,
    atmospheres: ['narrow alleys and shadows', 'desperate faces', 'the smell of poverty'],
    placeholderNames: ['The Slums', 'The Warrens', 'Low Town'],
  },
  garrison: {
    type: 'garrison',
    zone: 2,
    atmospheres: ['marching boots and drill calls', 'armored patrols', 'disciplined order'],
    placeholderNames: ['Garrison District', 'The Barracks Ward', 'Military Quarter'],
  },
  craftsmen: {
    type: 'craftsmen',
    zone: 2,
    atmospheres: ['hammer and bellows', 'sawdust and forge smoke', 'workshops and apprentices'],
    placeholderNames: ['Craftsmen District', 'The Workshops', 'Artisan Quarter'],
  },
  docks: {
    type: 'docks',
    zone: 2,
    atmospheres: ['creaking wood and salt air', 'ropes and nets', 'the cry of gulls'],
    placeholderNames: ['The Docks', 'Harbor District', 'Waterfront'],
  },
  farmland: {
    type: 'farmland',
    zone: 3,
    atmospheres: ['fields of grain', 'livestock and fences', 'the scent of earth'],
    placeholderNames: ['The Farmlands', 'The Fields', 'Outer Farms'],
  },
};

// ============================================================================
// PER-TIER TEMPLATES
// ============================================================================

export const TIER_TEMPLATES: Record<SettlementTier, TierTemplate> = {
  hamlet: {
    districtCount: [1, 2],
    buildingCount: [3, 8],
    hasWalls: false,
    hasInnerWall: false,
    entryRoads: [1, 2],
    requiredBuildings: ['tavern', 'shrine', 'well', 'general_store'],
    districtPool: ['center', 'farmland'],
  },
  village: {
    districtCount: [2, 4],
    buildingCount: [8, 18],
    hasWalls: false,
    hasInnerWall: false,
    entryRoads: [2, 3],
    requiredBuildings: ['tavern', 'shrine', 'well', 'general_store', 'blacksmith', 'guard_post', 'market'],
    districtPool: ['center', 'market', 'residential', 'farmland', 'craftsmen'],
  },
  town: {
    districtCount: [4, 7],
    buildingCount: [18, 35],
    hasWalls: true,
    hasInnerWall: false,
    entryRoads: [2, 4],
    requiredBuildings: [
      'tavern', 'tavern', 'temple', 'keep', 'inn', 'stable', 'fountain',
      'blacksmith', 'market', 'guard_post', 'general_store', 'well',
    ],
    districtPool: ['center', 'market', 'residential', 'noble', 'temple', 'craftsmen', 'garrison', 'slums'],
  },
  city: {
    districtCount: [6, 12],
    buildingCount: [35, 60],
    hasWalls: true,
    hasInnerWall: true,
    entryRoads: [3, 4],
    requiredBuildings: [
      'tavern', 'tavern', 'tavern', 'temple', 'temple', 'castle', 'guild_hall',
      'barracks', 'library', 'apothecary', 'inn', 'inn', 'blacksmith', 'blacksmith',
      'market', 'market', 'stable', 'fountain', 'fountain', 'guard_post', 'guard_post',
      'general_store', 'general_store', 'well', 'well',
    ],
    districtPool: [
      'center', 'market', 'residential', 'noble', 'temple', 'craftsmen',
      'garrison', 'slums', 'docks', 'farmland',
    ],
  },
};

// ============================================================================
// BUILDING SIZE → FOOTPRINT DIMENSIONS
// ============================================================================

export const BUILDING_FOOTPRINTS: Record<BuildingSize, { width: number; height: number }> = {
  small: { width: 4, height: 3 },
  medium: { width: 6, height: 5 },
  large: { width: 8, height: 7 },
};

// ============================================================================
// DISTRICT COLORS (for rendering)
// ============================================================================

export const DISTRICT_COLORS: Record<DistrictType, string> = {
  center: '#8B7355',      // warm brown
  market: '#D4A574',      // golden tan
  residential: '#9CAF88', // sage green
  noble: '#B8A9C9',       // light purple
  temple: '#F5DEB3',      // wheat
  slums: '#8B8682',       // gray
  garrison: '#A0522D',    // sienna
  craftsmen: '#CD853F',   // peru
  docks: '#87CEEB',       // sky blue
  farmland: '#90B77D',    // muted green
};

// ============================================================================
// BUILDING COLORS (for rendering)
// ============================================================================

export const BUILDING_COLORS: Record<BuildingCategory, string> = {
  tavern: '#C19A6B',
  inn: '#B5835A',
  temple: '#E8D5A3',
  shrine: '#E8D5A3',
  market: '#DAA520',
  blacksmith: '#8B4513',
  apothecary: '#6B8E23',
  guild_hall: '#CD853F',
  guard_post: '#A0522D',
  barracks: '#8B4513',
  keep: '#696969',
  castle: '#808080',
  warehouse: '#A0522D',
  stable: '#8B7355',
  library: '#D2B48C',
  well: '#4682B4',
  fountain: '#5F9EA0',
  gate: '#696969',
  tower: '#808080',
  dock: '#8B7355',
  farm: '#90B77D',
  mill: '#C19A6B',
  tannery: '#8B6914',
  residence: '#BC8F8F',
  manor: '#B8A9C9',
  ruin: '#808080',
  monument: '#C0C0C0',
  graveyard: '#696969',
  general_store: '#B5835A',
};
